import React, { useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import SimpleMobileGallery from './SimpleMobileGallery';
import ShareDialog from './ShareDialog';
interface ArticleHeaderProps {
  title: string;
  subtitle: string;
  category: string;
  readTime: number;
  coverImage: string;
  images?: string[]; // All images for mobile gallery
}
const ArticleHeader: React.FC<ArticleHeaderProps> = ({
  title,
  subtitle,
  category,
  readTime,
  coverImage,
  images = []
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const galleryRef = useRef<{
    handleTouchStart: (e: React.TouchEvent) => void;
    handleTouchMove: (e: React.TouchEvent) => void;
    handleTouchEnd: () => void;
  } | null>(null);

  // For mobile, use the gallery if images are provided, otherwise fallback to cover image
  const shouldShowGallery = isMobile && images.length > 1;

  // Pass through touch events to the gallery
  const handleTouchStart = (e: React.TouchEvent) => {
    if (shouldShowGallery && galleryRef.current) {
      galleryRef.current.handleTouchStart(e);
    }
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (shouldShowGallery && galleryRef.current) {
      galleryRef.current.handleTouchMove(e);
    }
  };
  const handleTouchEnd = () => {
    if (shouldShowGallery && galleryRef.current) {
      galleryRef.current.handleTouchEnd();
    }
  };
  return <div className="w-full">
      {/* Image container */}
      <div className="relative h-[50vh] md:h-[80vh] w-full overflow-hidden">
        <div className="absolute inset-0 max-w-full">
          <div className="relative w-full h-full" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            {/* Mobile: Swipeable gallery, Desktop: Single cover image */}
            {shouldShowGallery ? <SimpleMobileGallery ref={galleryRef} images={images} title={title} /> : <img src={coverImage} alt={title} className="w-full h-full object-cover" loading="lazy" />}
          </div>
        </div>
      </div>
      
      {/* Content below image */}
      <div className="article-container pt-6 py-[17px]">
        <h2 className="text-2xl md:text-3xl mb-3 md:mb-4 text-foreground font-chroma font-normal leading-tight md:leading-normal">
          {title}
        </h2>
        <hr className="border-t border-border mb-3 md:mb-4" />
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center">
            <Link 
              to={`/browse?category=${encodeURIComponent(category)}`}
              className="border border-foreground/20 bg-background text-foreground text-xs font-medium px-2 py-1 rounded-full hover:bg-foreground/5 transition-colors cursor-pointer"
            >
              {category}
            </Link>
            <span className="ml-3 text-muted-foreground text-xs md:text-sm flex items-center">
              <Clock size={isMobile ? 12 : 14} className="mr-1" />
              {readTime} min read
            </span>
          </div>
          <ShareDialog title={title} image={coverImage} description={subtitle} />
        </div>
        <hr className="border-t border-border mb-3 md:mb-4" />
      </div>
    </div>;
};
export default ArticleHeader;